import {
  ExistingProject,
  ProjectSettingsMap,
  ProjectUploadItem,
  SelectedProjectSummary,
  StorageUsageState,
  SubscriptionInfo,
} from '../models/batchProjectUpload';
import { DetectedProject, suggestProjectName } from './projectDetection';

export const isDuplicateProjectName = (
  existingProjects: ExistingProject[],
  projectName: string
): boolean => {
  const normalized = projectName.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return existingProjects.some(
    (existing) => existing.name.trim().toLowerCase() === normalized
  );
};

export const buildInitialProjectSettings = (
  detectedProjects: DetectedProject[],
  existingProjects: ExistingProject[]
): ProjectSettingsMap => {
  const initialSettings: ProjectSettingsMap = {};

  detectedProjects.forEach((project) => {
    const suggestedName = suggestProjectName(project.projectName);
    const duplicate = isDuplicateProjectName(existingProjects, suggestedName);

    initialSettings[project.folderPath] = {
      projectName: suggestedName,
      description: `${project.dawType || 'DAW'} project with ${
        project.fileCount
      } files`,
      isDuplicate: duplicate,
      duplicateAction: duplicate ? 'skip' : 'replace',
    };
  });

  return initialSettings;
};

export const getAutoRenamedProjectName = (
  existingProjects: ExistingProject[],
  baseName: string
): string => {
  const normalizedBase = baseName || 'Project';
  let newName = `${normalizedBase} (Copy)`;
  let counter = 2;

  while (isDuplicateProjectName(existingProjects, newName)) {
    newName = `${normalizedBase} (Copy ${counter})`;
    counter += 1;
  }

  return newName;
};

export const buildSelectedUploadItems = (
  detectedProjects: DetectedProject[],
  selectedProjects: Set<string>,
  projectSettings: ProjectSettingsMap
): ProjectUploadItem[] => {
  return detectedProjects
    .filter((project) => selectedProjects.has(project.folderPath))
    .map((project) => {
      const settings = projectSettings[project.folderPath] || {};
      const isDuplicate = settings.isDuplicate ?? false;

      return {
        detectedProject: project,
        projectName:
          settings.projectName || suggestProjectName(project.projectName),
        description: settings.description || '',
        isDuplicate,
        duplicateAction:
          settings.duplicateAction || (isDuplicate ? 'skip' : 'replace'),
        rendersFolderName: settings.rendersFolderName,
      };
    });
};

export const buildSelectedProjectSummary = (
  detectedProjects: DetectedProject[],
  selectedProjects: Set<string>,
  projectSettings: ProjectSettingsMap
): SelectedProjectSummary => {
  const selected = detectedProjects.filter((project) =>
    selectedProjects.has(project.folderPath)
  );

  const totalSelectedFiles = selected.reduce(
    (sum, project) => sum + project.fileCount,
    0
  );
  const totalSelectedSize = selected.reduce(
    (sum, project) => sum + project.totalSize,
    0
  );

  const dawDistribution = selected.reduce(
    (acc, project) => {
      if (project.dawType) {
        acc[project.dawType] = (acc[project.dawType] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  const duplicateCount = selected.filter(
    (project) => projectSettings[project.folderPath]?.isDuplicate
  ).length;

  return {
    selectedCount: selectedProjects.size,
    totalDetected: detectedProjects.length,
    totalSelectedFiles,
    totalSelectedSize,
    dawDistribution,
    duplicateCount,
  };
};

export const calculateStorageUsage = (
  subscription: SubscriptionInfo | null | undefined,
  totalSelectedSize: number
): StorageUsageState => {
  const currentStorageUsed = subscription?.storageUsed ?? 0;
  const storageLimit = subscription?.storageLimit ?? 0;
  const availableStorage = storageLimit - currentStorageUsed;
  const storageAfterUpload = currentStorageUsed + totalSelectedSize;
  const storagePercentageAfterUpload =
    storageLimit > 0 ? (storageAfterUpload / storageLimit) * 100 : 0;
  const wouldExceedLimit = totalSelectedSize > availableStorage;
  const isApproachingLimit = storagePercentageAfterUpload > 90;

  return {
    currentStorageUsed,
    storageLimit,
    availableStorage,
    storageAfterUpload,
    storagePercentageAfterUpload,
    wouldExceedLimit,
    isApproachingLimit,
  };
};

/**
 * Sort projects by the specified field and direction
 */
export const sortProjects = (
  projects: DetectedProject[],
  sortField?: string,
  ascending?: boolean
): DetectedProject[] => {
  const field = sortField || 'name';
  const isAscending = ascending !== false; // Default to true if undefined

  const sorted = [...projects].sort((a, b) => {
    let compareResult = 0;

    switch (field) {
      case 'name':
        compareResult = a.projectName.localeCompare(b.projectName);
        break;

      case 'folder':
        compareResult = a.folderPath.localeCompare(b.folderPath);
        break;

      case 'dawType':
        // Sort alphabetically by DAW type string
        compareResult = a.dawType.localeCompare(b.dawType);
        break;

      case 'size':
        compareResult = a.totalSize - b.totalSize;
        break;

      case 'created':
        // Handle undefined - put undefined values last
        if (
          a.earliestModified === undefined &&
          b.earliestModified === undefined
        ) {
          compareResult = 0;
        } else if (a.earliestModified === undefined) {
          compareResult = 1; // a goes after b
        } else if (b.earliestModified === undefined) {
          compareResult = -1; // a goes before b
        } else {
          compareResult = a.earliestModified - b.earliestModified;
        }
        break;

      case 'updated':
        // Handle undefined - put undefined values last
        if (a.latestModified === undefined && b.latestModified === undefined) {
          compareResult = 0;
        } else if (a.latestModified === undefined) {
          compareResult = 1; // a goes after b
        } else if (b.latestModified === undefined) {
          compareResult = -1; // a goes before b
        } else {
          compareResult = a.latestModified - b.latestModified;
        }
        break;

      default:
        // Default to name sort
        compareResult = a.projectName.localeCompare(b.projectName);
    }

    return isAscending ? compareResult : -compareResult;
  });

  return sorted;
};
